<script setup>
import { computed } from 'vue';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { STATUS } from 'dashboard/store/constants';
import { useI18n } from 'vue-i18n';
import {
  formatTrend,
  getMetricIcon,
  getTrendTone,
  TREND_TONE_CLASSES,
} from './trendHelper';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({}),
  },
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
  summaryFetchingKey: {
    type: String,
    default: 'getAccountSummaryFetchingStatus',
  },
  // tile: standalone KPI card; inline: compact value + trend for card headers.
  variant: {
    type: String,
    default: 'tile',
    validator: value => ['tile', 'inline'].includes(value),
  },
});

const { t } = useI18n();

const { calculateTrend, displayMetric, isAverageMetricType, fetchingStatus } =
  useReportMetrics(props.accountSummaryKey, props.summaryFetchingKey);

const isFetching = computed(() => fetchingStatus.value === STATUS.FETCHING);
const isFailed = computed(() => fetchingStatus.value === STATUS.FAILED);
const isFinished = computed(() => fetchingStatus.value === STATUS.FINISHED);

const trend = computed(() =>
  isFinished.value && props.metric.trend ? calculateTrend(props.metric.KEY) : 0
);
const trendTone = computed(() =>
  getTrendTone(trend.value, isAverageMetricType(props.metric.KEY))
);
const trendIcon = computed(() =>
  trend.value > 0
    ? 'i-hugeicons-arrow-up-right-01'
    : 'i-hugeicons-arrow-down-right-01'
);
const trendLabel = computed(
  () => `${formatTrend(trend.value)} ${t('REPORT.TREND_VS_PREVIOUS')}`
);
const icon = computed(() => getMetricIcon(props.metric.KEY));
</script>

<template>
  <div
    v-if="variant === 'tile'"
    data-test-id="chartStatsTile"
    class="flex min-w-0 flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3.5 dark:border-n-strong"
  >
    <h3
      class="m-0 flex min-w-0 items-center gap-1.5 text-xs font-medium text-n-slate-11"
    >
      <span
        :class="icon"
        class="size-4 shrink-0 text-n-slate-10"
        aria-hidden="true"
      />
      <span class="truncate">{{ metric.NAME }}</span>
    </h3>
    <div class="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1">
      <span
        v-if="isFetching"
        class="h-7 w-20 rounded-md bg-n-alpha-2 motion-safe:animate-pulse"
        role="status"
        :aria-label="t('REPORT.LOADING_CHART')"
      />
      <span v-else-if="isFailed" class="text-sm text-n-ruby-11">
        {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
      </span>
      <span
        v-else-if="isFinished"
        class="text-2xl font-semibold tabular-nums text-n-slate-12"
      >
        {{ displayMetric(metric.KEY) }}
      </span>
    </div>
    <div
      v-if="trend"
      class="flex flex-wrap items-center gap-1.5 text-xs text-n-slate-11"
    >
      <span
        class="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-medium tabular-nums"
        :class="TREND_TONE_CLASSES[trendTone]"
      >
        <span :class="trendIcon" class="size-3.5" aria-hidden="true" />
        {{ formatTrend(trend) }}
      </span>
      <span>{{ t('REPORT.TREND_VS_PREVIOUS') }}</span>
    </div>
  </div>

  <div v-else data-test-id="chartStatsInline" class="flex items-center gap-2">
    <span
      v-if="isFetching"
      class="h-6 w-16 rounded-md bg-n-alpha-2 motion-safe:animate-pulse"
      role="status"
      :aria-label="t('REPORT.LOADING_CHART')"
    />
    <span
      v-else-if="isFailed"
      v-tooltip="t('REPORT.SUMMARY_FETCHING_FAILED')"
      class="i-hugeicons-alert-circle size-4 text-n-ruby-11"
      role="img"
      :aria-label="t('REPORT.SUMMARY_FETCHING_FAILED')"
    />
    <span
      v-else-if="isFinished"
      class="text-lg font-semibold tabular-nums text-n-slate-12"
    >
      {{ displayMetric(metric.KEY) }}
    </span>
    <span
      v-if="trend"
      v-tooltip="trendLabel"
      class="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums"
      :class="TREND_TONE_CLASSES[trendTone]"
      :aria-label="trendLabel"
    >
      <span :class="trendIcon" class="size-3.5" aria-hidden="true" />
      {{ formatTrend(trend) }}
    </span>
  </div>
</template>
